import { useEffect, useState } from "react";
import axios from "axios";
import { camelCase } from "lodash";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import {
  pickRefineStats,
  readScopedWizardValue,
  readWizardStorage,
  saveScopedWizardValue,
} from "./wizardStorage";

const GOAL_TYPE_ALIASES = {
  netIncome: "currentNetIncomeGoal",
  outboundCalls: "calls",
  doorKnocking: "doorKnocks",
};

const BASE_GOALS = {
  avgCommissionPercent: 0,
  avgSalePrice: 0,
  brivityActivityGoalTypes: [],
  brokerageCap: 0,
  brokerageSplitPercentage: 0,
  buyerCommissionSplit: 0,
  currentNetIncomeGoal: 0,
  goalTypes: [],
  loggedActivityGoalTypes: [],
  pendingToCloseRatio: 0,
  royaltyCap: 0,
  royaltyPercentage: 0,
  sellerCommissionSplit: 0,
  sellerToTotalClosingRatio: 0,
  unitSources: {},
};

const TEAM_DEFAULT_STAT_KEYS = [
  "callsLastTwelveMonths",
  "contactsLastTwelveMonths",
  "appointmentsSetLastTwelveMonths",
  "appointmentsHeldLastTwelveMonths",
  "signingsLastTwelveMonths",
  "unitsPendedLastTwelveMonths",
  "unitsClosedLastTwelveMonths",
];

const mergeSavedGoals = (fetchedGoals, savedGoals = {}) => {
  const mergedGoals = { ...fetchedGoals, ...savedGoals };

  TEAM_DEFAULT_STAT_KEYS.forEach((key) => {
    mergedGoals[key] = fetchedGoals[key];
  });
  mergedGoals.agentSalesFunnelStats = fetchedGoals.agentSalesFunnelStats;

  return mergedGoals;
};

const getWizard = (...config) =>
  caseTransformingAxios.get("/api/v4/goals/wizard", ...config).then((res) => res.data);

const saveGoal = (...config) =>
  caseTransformingAxios.post("/api/v4/goals/upsert", ...config).then((res) => res.data);

const upsertWizardTeamDefaults = (...config) =>
  caseTransformingAxios.post("/api/v4/goals/upsert_wizard_team_defaults", ...config).then((res) => res.data);

const useWizard = ({ agentId, year }) => {
  const scopeKey = `${year}:${String(agentId ?? "self")}`;
  const [goals, setGoals] = useState({
    ...BASE_GOALS,
  });
  const [apiGoals, setApiGoals] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadedScopeKey, setLoadedScopeKey] = useState(null);

  useEffect(() => {
    setLoading(true);
    setLoadedScopeKey(null);
    getWizard({ params: { userUuid: agentId, year } })
      .then((data) => {
        setLoading(false);
        setLoadedScopeKey(scopeKey);
        setApiGoals(data);
        const stored = readWizardStorage();
        const s = stored.teamProductionWizard;
        const hasSaved =
          stored.status === "in_progress" &&
          s &&
          String(s.year) === String(year) &&
          String(s.agentId ?? "") === String(agentId ?? "") &&
          s.goals;
        const savedStats = readScopedWizardValue(agentId, year, "refineStats");
        const apiStats = pickRefineStats(data);
        const draftStats = hasSaved ? pickRefineStats(s.goals) : {};
        const hasDraftStats = Object.keys(draftStats).length > 0;
        if (!savedStats) {
          saveScopedWizardValue(
            agentId,
            year,
            "refineStats",
            hasDraftStats ? draftStats : apiStats,
            hasDraftStats,
          );
        }
        let statsOverrides = {};
        if (savedStats?.modified) statsOverrides = savedStats.values;
        else if (!savedStats && hasDraftStats) statsOverrides = draftStats;
        const refinedStats = {
          ...apiStats,
          ...statsOverrides,
        };
        const savedSources = readScopedWizardValue(agentId, year, "unitSources");
        const defaultSources = data.unitSources ?? (hasSaved ? s.goals.unitSources : {}) ?? {};
        if (!savedSources) saveScopedWizardValue(agentId, year, "unitSources", defaultSources, false);
        const unitSources = savedSources?.modified ? savedSources.values : defaultSources;
        setGoals({ ...(hasSaved ? mergeSavedGoals(data, s.goals) : data), ...refinedStats, unitSources });
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setLoading(false);
          setLoadedScopeKey(scopeKey);
          setErrors({ global: "Failed to load Goals" });
        }
      });
  }, [agentId, scopeKey, year]);

  return {
    actions: {
      setLocalGoals: setGoals,
      saveWizardStepOne: ({ currentNetIncomeGoal }) => {
        const netIncomeGoalType = (goals.goalTypes || []).find(
          (goalType) => camelCase(goalType.name) === "netIncome",
        );
        const nextNetIncomeGoal = Number(currentNetIncomeGoal) || 0;

        setLoading(true);

        return Promise.all([
          upsertWizardTeamDefaults({
            userUuid: agentId,
            goalWizardTeamDefaults: {},
          }),
          ...(netIncomeGoalType
            ? [
                saveGoal({
                  userUuid: agentId,
                  goalTypeId: netIncomeGoalType.id,
                  goalValue: nextNetIncomeGoal,
                  year,
                }),
              ]
            : []),
        ])
          .then(() => {
            setLoading(false);
            return true;
          })
          .catch(() => {
            setLoading(false);
            setErrors({ global: "Failed to save Goals" });
            return false;
          });
      },
      saveGoals: (recommendedGoals) => {
        const allGoalTypes = [
          ...(goals.goalTypes || []),
          ...(goals.brivityActivityGoalTypes || []),
          ...(goals.loggedActivityGoalTypes || []),
        ];

        const getGoalValueForType = (goalTypeName) => {
          const rawKey = camelCase(goalTypeName);
          const key = GOAL_TYPE_ALIASES[rawKey] || rawKey;

          const directValue = recommendedGoals[key];
          if (directValue !== undefined && directValue !== null && directValue !== "") return directValue;

          const brivityValue = recommendedGoals.brivityActivityGoals?.[key];
          if (brivityValue !== undefined && brivityValue !== null && brivityValue !== "") return brivityValue;

          const loggedValue = recommendedGoals.loggedActivityGoals?.[key];
          if (loggedValue !== undefined && loggedValue !== null && loggedValue !== "") return loggedValue;

          return null;
        };

        setLoading(true);
        return Promise.all(
          allGoalTypes
            .filter((gt) => gt.name !== "closed_gci")
            .map((gt) => ({
              goalTypeId: gt.id,
              goalValue: getGoalValueForType(gt.name),
            }))
            .filter(({ goalValue }) => goalValue !== null)
            .map(({ goalTypeId, goalValue }) =>
              saveGoal({
                userUuid: agentId,
                goalTypeId,
                goalValue,
                year,
              }),
            ),
        )
          .then(() => {
            setLoading(false);
            return true;
          })
          .catch(() => {
            setLoading(false);
            setErrors({ global: "Failed to save Goals" });
            return false;
          });
      },
    },
    errors,
    goals,
    apiGoals,
    loading,
    loadedScopeKey,
  };
};

export default useWizard;
